/**
 * LeadDetailsView - Full-page view for lead details
 */

import React, { useState, useCallback, useMemo, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  List,
  ListItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from "@mui/material";
import {
  ArrowBack,
  WhatsApp,
  Timeline,
  ExpandMore,
} from "@mui/icons-material";
import { format } from "date-fns";
import { MediaItemsGallery } from "../../../components/media";

// Import hooks
import {
  useLead,
  useUpdateLeadInfo,
  useUpdateLeadStatus,
} from "../hooks/useLeads.js";
import { useNotifications } from "../../notifications/context/NotificationContext.jsx";
import LeadDownloadDialog from "../components/LeadDownloadDialog.jsx";
import { leadDownloadService } from "../services/downloadService.js";
import {
  openLeadItemsConfirmationLink,
} from "../utils/whatsappLinkGenerator.js";
// SOP #1: New components
import LeadCompletenessIndicator from "../components/LeadCompletenessIndicator.jsx";
import LeadOpsActivitySection from "../components/LeadOpsActivitySection.jsx";
import CommunicationTemplateGenerator from "../components/CommunicationTemplateGenerator.jsx";
import LeadNotesPreview from "../components/LeadNotesPreview.jsx";
import ShareLeadWhatsAppModal from "../components/ShareLeadWhatsAppModal.jsx";
import CopyPublicLeadSummaryDialog from "../components/CopyPublicLeadSummaryDialog.jsx";
import LeadActionToolbar from "../components/LeadActionToolbar.jsx";
import OperationalStatusBadge from "../components/OperationalStatusBadge.jsx";
import { ConfigProvider } from "antd";
import { bubakiiTheme } from "../../../theme/bubakiiTheme.js";
// Centralized item store hook
import { useItems } from "../../../hooks/useItems.js";
// Customer notes component
import CustomerNotesPanel from "../../customers/components/CustomerNotesPanel.jsx";
import SharedLoadLeadBadge, {
  SharedLoadLeadDetail,
} from "../components/SharedLoadLeadBadge.jsx";
import { buildAdminJobBoardShareUrl } from "../../../shared/utils/pwaLinks.js";
import LeadElementChecklist from "../intake/LeadElementChecklist.jsx";
import { getLeadIntakeElementManifest } from "../intake/leadIntakeElementsService.js";
import {
  seedConfirmationsForLeadEdit,
} from "../intake/elementChecklistHelpers.js";
import { convertLeadToOpportunity } from "../services/leadService.js";
import { GoogleAddressAutocomplete } from "../../../components/AddressInput/GoogleAddressAutocomplete.jsx";
import UnsafeAreaAlert from "../../../shared/components/UnsafeAreaAlert.jsx";
import BiddingPanel from "../bidding/components/BiddingPanel.jsx";
import LeadFactsRow from "../components/LeadFactsRow.jsx";
import LeadValueProvidersStrip from "../components/LeadValueProvidersStrip.jsx";
import {
  JobJourneyStrip,
  pickEntityId,
  quoteDisplayFrom,
  seedFromLead,
  useJobJourneyLinks,
} from "../../../shared/jobJourney/index.js";
import {
  fetchQuote,
  fetchQuotes,
} from "../../quotes/repositories/quoteRepository.js";
import { formatServiceTypeLabel } from "../../../shared/utils/serviceTypeLanguage.js";
import EstimatePricingFab from "../../quotes/components/EstimatePricingFab.jsx";
import { formatLeadItemsList } from "../../../shared/utils/leadItems.js";
import {
  pickLeadNativeQuoteId,
  pickTrustedQuoteIdForLead,
  selectLinkedQuoteForLead,
} from "../services/linkedQuoteForLead.js";

const FULFILMENT_DISCOVERY_CATEGORY = "fulfilment_discovery";

const formatLeadCategory = (category) =>
  String(category || "moving")
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

/** True when fulfilment discovery has at least one populated field to display. */
const hasFulfilmentDiscoveryInfo = (discovery) => {
  if (!discovery || typeof discovery !== "object") return false;
  const {
    companyName,
    provincesServed,
    estimatedMonthlyDeliveries,
    currentDeliveryMethod,
    preferredContactMethod,
    biggestChallenge,
  } = discovery;
  return Boolean(
    companyName ||
      (Array.isArray(provincesServed) && provincesServed.length > 0) ||
      estimatedMonthlyDeliveries ||
      currentDeliveryMethod ||
      preferredContactMethod ||
      biggestChallenge
  );
};

/**
 * LeadDetailsView Component
 */
const LeadDetailsView = () => {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Get initial lead data from location state (optimistic update)
  const initialLead = location.state?.lead;

  // State
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [actionDialog, setActionDialog] = useState({
    open: false,
    type: "",
    data: {},
  });
  const [downloadDialog, setDownloadDialog] = useState(false);
  const [templateDialog, setTemplateDialog] = useState(false);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const [isDownloadingZip, setIsDownloadingZip] = useState(false);
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [shareWhatsAppOpen, setShareWhatsAppOpen] = useState(false);
  const [publicCopyOpen, setPublicCopyOpen] = useState(false);
  const [convertingToOpportunity, setConvertingToOpportunity] = useState(false);
  const [linkedQuote, setLinkedQuote] = useState(null);
  const [providersExpanded, setProvidersExpanded] = useState(false);
  const [providersRefreshKey, setProvidersRefreshKey] = useState(0);
  const [manifestElements, setManifestElements] = useState([]);
  const [manifestCard, setManifestCard] = useState(null);
  const [manifestLoading, setManifestLoading] = useState(false);
  const [manifestError, setManifestError] = useState(null);

  // Hooks
  const { lead, loading, error, refetch } = useLead(leadId, initialLead);
  useEffect(() => {
    setLinkedQuote(null);
    setIsEditing(false);
    setEditData({});
    setActionDialog({ open: false, type: "", data: {} });
  }, [leadId]);
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("panel") === "bidding") {
      setProvidersExpanded(true);
    }
  }, [location.search]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setManifestLoading(true);
      setManifestError(null);
      try {
        const result = await getLeadIntakeElementManifest();
        if (cancelled) return;
        setManifestElements(result.elements || []);
        setManifestCard(result.card || null);
      } catch (err) {
        if (cancelled) return;
        setManifestError(
          err?.response?.data?.error ||
            err.message ||
            "Failed to load card elements",
        );
      } finally {
        if (!cancelled) setManifestLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!isEditing || !manifestElements.length) return;
    setEditData((prev) => {
      const seeded = seedConfirmationsForLeadEdit({
        elements: manifestElements,
        confirmations: prev.elementConfirmations?.length
          ? prev.elementConfirmations
          : lead?.qualification?.elementConfirmations,
        pricingSelections: lead?.qualification?.pricingSelections,
      });
      return { ...prev, elementConfirmations: seeded };
    });
  }, [
    isEditing,
    manifestElements,
    lead?.qualification?.elementConfirmations,
    lead?.qualification?.pricingSelections,
  ]);

  const { updateInfo, loading: updateLoading } = useUpdateLeadInfo();
  const { updateStatus, loading: statusLoading } = useUpdateLeadStatus();
  const { refresh: refreshNotifications } = useNotifications();
  const leadCategory = lead?.category || lead?.type || "moving";
  const isFulfilmentDiscovery =
    leadCategory === FULFILMENT_DISCOVERY_CATEGORY;
  const fulfilmentDiscovery = lead?.qualification?.fulfilmentDiscovery;
  const showFulfilmentDiscovery =
    hasFulfilmentDiscoveryInfo(fulfilmentDiscovery);

  // Fetch items from centralized store using itemSetId (with fallback to lead.items)
  const { items: fetchedItems, loading: itemsLoading } = useItems(
    lead?.itemSetId,
    lead?.items || []
  );

  const leadForWhatsApp = useMemo(() => {
    if (!lead) return null;
    const itemsToShare =
      fetchedItems.length > 0 ? fetchedItems : lead.items || [];
    return {
      ...lead,
      items: itemsToShare,
    };
  }, [lead, fetchedItems]);

  const jobBoardUrl = useMemo(() => {
    if (lead?.bidding?.status !== "open") return null;
    return buildAdminJobBoardShareUrl(lead?.bidding?.publicJobId);
  }, [lead?.bidding?.publicJobId, lead?.bidding?.status]);

  // Handlers
  const handleBack = useCallback(() => {
    navigate("/leads");
  }, [navigate]);

  const handleEdit = useCallback(() => {
    // Use fetchedItems from centralized store (via itemSetId) with fallback to lead.items
    const itemsToEdit =
      fetchedItems.length > 0 ? fetchedItems : lead?.items || [];

    setEditData({
      name: lead?.name || "",
      notes: lead?.notes || "",
      category: lead?.category || lead?.type || "",
      status: lead?.status || "new",
      phone: lead?.phone || "",
      moving_from: lead?.moving_from || lead?.pickupAddress?.address || "",
      moving_to: lead?.moving_to || lead?.deliveryAddress?.address || "",
      pickupAddressData: lead?.pickupAddress || null,
      deliveryAddressData: lead?.deliveryAddress || null,
      move_date: lead?.move_date || "",
      contactInfo: {
        email: lead?.contactInfo?.email || lead?.email || "",
      },
      // Text inventory only — media attachments stay separate and are preserved on save
      // Format: "2 queen size beds" or just "stove" (if quantity is 1)
      items: itemsToEdit
        .filter((item) => {
          if (typeof item === "string") return item.trim().length > 0;
          if (!item || typeof item !== "object") return false;
          if (item.type && item.type !== "text") return false;
          const content = item.content || item.description || "";
          return content.trim().length > 0;
        })
        .map((item) => {
          if (typeof item === "string") return item.trim();
          const content = item.content || item.description || "";
          const quantity =
            item.quantity !== undefined && item.quantity !== null
              ? item.quantity
              : 1;
          // Include quantity in the string if it's greater than 1
          return quantity > 1 ? `${quantity} ${content}` : content;
        }),
      elementConfirmations: Array.isArray(
        lead?.qualification?.elementConfirmations,
      )
        ? lead.qualification.elementConfirmations
        : [],
      serviceType: lead?.qualification?.serviceType || lead?.serviceType || null,
    });
    setIsEditing(true);
  }, [lead, fetchedItems]);

  const handleSave = useCallback(async () => {
    try {
      const existingItems =
        (fetchedItems.length > 0 ? fetchedItems : lead?.items) || [];
      const existingMediaItems = existingItems.filter(
        (item) =>
          item &&
          typeof item === "object" &&
          ((item.type && item.type !== "text") ||
            item.previewUrl ||
            item.downloadUrl ||
            item.mimeType?.startsWith?.("image/") ||
            item.mimeType === "application/pdf")
      );

      // Prepare update data - map editData to backend format
      const updateData = {
        name: editData.name,
        phone: editData.phone,
        email: editData.contactInfo?.email,
        moving_from: editData.moving_from,
        moving_to: editData.moving_to,
        pickupAddress: editData.pickupAddressData || null,
        deliveryAddress: editData.deliveryAddressData || null,
        move_date: editData.move_date,
        category: editData.category,
        type: editData.category, // Also update type field
        status: editData.status,
        notes: editData.notes,
        // Parse text inventory from strings; keep existing media attachments
        items: [
          ...(Array.isArray(editData.items)
            ? editData.items
                .map((itemStr) => (itemStr || "").toString().trim())
                .filter((itemStr) => itemStr.length > 0)
                .map((itemStr) => {
                  // Parse quantity from string like "2 queen size beds" or "1 stove"
                  const quantityMatch = itemStr.match(/^(\d+)\s+(.+)$/);
                  if (quantityMatch) {
                    const quantity = parseInt(quantityMatch[1], 10);
                    const content = quantityMatch[2].trim();
                    return { type: "text", content, quantity };
                  }
                  // No quantity found, default to 1
                  return { type: "text", content: itemStr, quantity: 1 };
                })
            : []),
          ...existingMediaItems,
        ],
        qualification: {
          ...(editData.serviceType
            ? { serviceType: editData.serviceType }
            : {}),
          elementConfirmations: Array.isArray(editData.elementConfirmations)
            ? editData.elementConfirmations
            : [],
        },
      };
      const result = await updateInfo(leadId, updateData);

      if (result.success) {
        setNotification({
          open: true,
          message: "Lead information updated successfully",
          severity: "success",
        });
        setIsEditing(false);
        refetch();
        // Refresh notifications in case there are any related updates
        refreshNotifications();
      } else {
        setNotification({
          open: true,
          message: result.error || "Failed to update lead",
          severity: "error",
        });
      }
    } catch (error) {
      setNotification({
        open: true,
        message: error.message || "Failed to update lead",
        severity: "error",
      });
    }
  }, [leadId, editData, updateInfo, refetch, refreshNotifications, fetchedItems, lead]);

  const handleCancel = useCallback(() => {
    setIsEditing(false);
    setEditData({});
  }, []);

  const handleFollowUpNavigate = useCallback(() => {
    const entityId = lead?.id || lead?._id;
    if (!entityId) return;
    const followUpId =
      lead?.followUpSummary?.nextFollowUpId ||
      lead?.followUpSummary?.latestFollowUpId;
    if (followUpId) {
      navigate(`/followups/${followUpId}`);
    } else {
      navigate(`/followups?entityType=lead&entityId=${entityId}`);
    }
  }, [navigate, lead]);

  const handleAction = useCallback(
    (type) => {
      if (type === "convert") {
        // Navigate to quote form with leadId pre-populated
        navigate(`/quotes/new?leadId=${leadId}`);
        return;
      }
      setActionDialog({ open: true, type, data: {} });
    },
    [navigate, leadId]
  );

  const handleActionConfirm = useCallback(async () => {
    const { type, data } = actionDialog;

    try {
      let result;

      switch (type) {
        case "accept":
          result = await updateStatus(leadId, {
            action: "accept",
            notes: data.notes || "Lead accepted",
          });
          break;

        case "reject":
          result = await updateStatus(leadId, {
            action: "reject",
            reason: data.reason || "Lead rejected",
            notes: data.notes,
          });
          break;

        // "convert" case removed - now redirects to quote form instead
        default:
          throw new Error("Unknown action type");
      }

      if (result.success) {
        setNotification({
          open: true,
          message: result.message || `${type} action completed successfully`,
          severity: "success",
        });
        refetch();
        // Refresh notifications to show the new notification
        refreshNotifications();
      } else {
        setNotification({
          open: true,
          message: result.error || `Failed to ${type} lead`,
          severity: "error",
        });
      }
    } catch (error) {
      setNotification({
        open: true,
        message: error.message || `Failed to ${type} lead`,
        severity: "error",
      });
    } finally {
      setActionDialog({ open: false, type: "", data: {} });
    }
  }, [actionDialog, leadId, updateStatus, refetch, refreshNotifications]);

  const handleActionCancel = useCallback(() => {
    setActionDialog({ open: false, type: "", data: {} });
  }, []);

  const handleDownload = useCallback(async (leadId, includeCustomerDetails) => {
    setIsDownloadingZip(true);
    try {
      await leadDownloadService.downloadLead(leadId, includeCustomerDetails);
      setNotification({
        open: true,
        message: "Lead download started successfully",
        severity: "success",
      });
    } catch (error) {
      setNotification({
        open: true,
        message: error.message || "Failed to download lead",
        severity: "error",
      });
    } finally {
      setIsDownloadingZip(false);
    }
  }, []);

  const handleDownloadPdf = useCallback(async () => {
    setIsDownloadingPdf(true);
    try {
      await leadDownloadService.downloadPdf(leadId, true);
      setNotification({
        open: true,
        message: "PDF download started successfully",
        severity: "success",
      });
    } catch (error) {
      setNotification({
        open: true,
        message: error.message || "Failed to download PDF",
        severity: "error",
      });
    } finally {
      setIsDownloadingPdf(false);
    }
  }, [leadId]);

  const handleDownloadClick = useCallback(() => {
    setDownloadDialog(true);
  }, []);

  const handleShareOnWhatsApp = useCallback(() => {
    if (!lead) {
      setNotification({
        open: true,
        message: "Lead data is not available",
        severity: "error",
      });
      return;
    }

    setShareWhatsAppOpen(true);
  }, [lead]);

  const handleNotificationClose = useCallback(() => {
    setNotification({ ...notification, open: false });
  }, [notification]);

  // Prepare items for table display
  // Use fetchedItems from centralized store (via itemSetId) with fallback to lead.items
  const itemsToDisplay = useMemo(
    () => (fetchedItems.length > 0 ? fetchedItems : lead?.items || []),
    [fetchedItems, lead?.items]
  );

  const MEDIA_ITEM_TYPES = useMemo(
    () =>
      new Set(["image", "video", "document", "audio", "spreadsheet", "csv"]),
    []
  );

  const isMediaItem = useCallback(
    (item) => {
      if (!item || typeof item !== "object") return false;
      if (MEDIA_ITEM_TYPES.has(item.type)) return true;
      if (item.previewUrl || item.downloadUrl) return true;
      if (item.mimeType?.startsWith?.("image/")) return true;
      if (item.mimeType === "application/pdf") return true;
      return false;
    },
    [MEDIA_ITEM_TYPES]
  );

  const mediaItems = useMemo(() => {
    const fromDisplay = (itemsToDisplay || []).filter(isMediaItem);
    const fromLead = (lead?.items || []).filter(isMediaItem);
    const byKey = new Map();
    [...fromLead, ...fromDisplay].forEach((item) => {
      const key =
        item.id ||
        item.previewUrl ||
        item.downloadUrl ||
        item.filename ||
        JSON.stringify(item);
      if (!byKey.has(key)) {
        byKey.set(key, {
          ...item,
          // Ensure gallery treats Firebase photos as images
          type:
            item.type ||
            (item.mimeType?.startsWith?.("image/") ? "image" : "document"),
        });
      }
    });
    return Array.from(byKey.values());
  }, [itemsToDisplay, lead?.items, isMediaItem]);

  // Normalize text inventory for table display (attachments shown separately)
  const tableItems = useMemo(
    () =>
      itemsToDisplay
        ?.filter((item) => {
          if (!item) return false;

          // Include string items with content
          if (typeof item === "string") {
            return item.trim().length > 0;
          }

          // Inventory table is text-only; media has its own gallery
          if (item.type && item.type !== "text") {
            return false;
          }

          const description = (
            item.description ||
            item.content ||
            item.caption ||
            item.filename ||
            ""
          ).trim();
          return description.length > 0;
        })
        .map((item, idx) => {
          // Normalize items for consistent table display
          if (typeof item === "string") {
            return {
              item: `Item ${idx + 1}`,
              description: item.trim(),
              quantity: 1,
              type: "text",
            };
          }

          // Extract item name/type
          const itemName = item.type
            ? item.type.charAt(0).toUpperCase() +
              item.type.slice(1).replace(/_/g, " ")
            : `Item ${idx + 1}`;

          // Extract description (prefer description, then content, then caption/filename)
          const description = (
            item.description ||
            item.content ||
            item.caption ||
            item.filename ||
            "N/A"
          ).trim();

          // Extract quantity (default to 1)
          const quantity = item.quantity || 1;

          return {
            item: itemName,
            description: description,
            quantity: quantity,
            type: item.type || "text",
          };
        }) || [],
    [itemsToDisplay]
  );

  // Handle items confirmation on WhatsApp (defined after tableItems)
  const handleConfirmItemsOnWhatsApp = useCallback(() => {
    if (!lead?.phone) {
      setNotification({
        open: true,
        message: "Customer phone number is required to send items confirmation",
        severity: "warning",
      });
      return;
    }

    if (!tableItems || tableItems.length === 0) {
      setNotification({
        open: true,
        message: "No items to confirm",
        severity: "warning",
      });
      return;
    }

    try {
      openLeadItemsConfirmationLink(tableItems, lead);
    } catch (error) {
      setNotification({
        open: true,
        message: error.message || "Failed to generate WhatsApp link",
        severity: "error",
      });
    }
  }, [lead, tableItems]);

  const getStatusLabel = (status) => {
    switch (status) {
      case "new":
        return "New";
      case "pending_identity":
        return "Waiting for email confirm";
      case "contacted":
        return "Contacted";
      case "qualified":
        return "Qualified";
      case "accepted":
        return "Accepted";
      case "rejected":
        return "Rejected";
      case "converted":
        return "Converted";
      default:
        return status;
    }
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), "MMM dd, yyyy HH:mm");
    } catch {
      return "Invalid date";
    }
  };

  const formatPhone = (phone) => {
    if (!phone) return "N/A";
    return phone.replace("+27", "0");
  };

  // Helper functions to check if lead is converted and get quote ID
  const getConvertedQuoteId = (lead) => {
    return (
      lead?.convertedQuoteId || lead?.tracking?.conversionData?.quoteId || null
    );
  };

  const isLeadConverted = (lead) => {
    return lead?.status === "converted" || !!getConvertedQuoteId(lead);
  };

  const getOpportunityId = (leadData) =>
    leadData?.tracking?.conversionData?.opportunityId || null;

  const trustedLinkedQuote = useMemo(
    () => selectLinkedQuoteForLead(linkedQuote, leadId),
    [linkedQuote, leadId]
  );
  const trustedQuoteId = useMemo(
    () =>
      pickTrustedQuoteIdForLead({
        linkedQuote,
        lead,
        leadId,
      }),
    [linkedQuote, lead, leadId]
  );

  const jobJourneyLinks = useJobJourneyLinks(
    lead
      ? seedFromLead(lead, {
          quoteId: pickEntityId(
            trustedLinkedQuote?.id,
            trustedLinkedQuote?._id,
            lead?.automatedPricing?.quoteId
          ),
          quoteDisplay: trustedLinkedQuote
            ? quoteDisplayFrom(trustedLinkedQuote)
            : null,
        })
      : { current: "lead", enabled: false }
  );

  // Load linked provisional/converted quote so Lead Ops cockpit uses
  // automated pricingExplanation instead of legacy pricingSuggestion.
  useEffect(() => {
    let cancelled = false;
    async function loadLinkedQuote() {
      if (!leadId) {
        setLinkedQuote(null);
        return;
      }
      // Only lead-native ids, and only once `lead` matches this route.
      // The view stays mounted across `/leads/:id` changes, so a stale
      // previous lead object must not supply the fetch-by-id target.
      const leadMatchesRoute =
        Boolean(lead) &&
        pickEntityId(lead?.id, lead?._id) === pickEntityId(leadId);
      const knownId = leadMatchesRoute ? pickLeadNativeQuoteId(lead) : null;
      try {
        if (knownId) {
          const byId = await fetchQuote(knownId);
          if (!cancelled && byId.success && byId.data) {
            const trusted = selectLinkedQuoteForLead(byId.data, leadId, {
              allowMissingLeadId: true,
            });
            if (trusted) {
              setLinkedQuote(trusted);
              return;
            }
          }
        }
        const listed = await fetchQuotes({ leadId, limit: 5 });
        if (cancelled) return;
        const rows = Array.isArray(listed.data) ? listed.data : [];
        const trustedRows = rows.filter((row) =>
          selectLinkedQuoteForLead(row, leadId, { allowMissingLeadId: true })
        );
        if (trustedRows.length === 0) {
          setLinkedQuote(null);
          return;
        }
        const sorted = [...trustedRows].sort((a, b) => {
          const at = new Date(a.updatedAt || a.createdAt || 0).getTime();
          const bt = new Date(b.updatedAt || b.createdAt || 0).getTime();
          return bt - at;
        });
        setLinkedQuote(sorted[0]);
      } catch {
        if (!cancelled) setLinkedQuote(null);
      }
    }
    loadLinkedQuote();
    return () => {
      cancelled = true;
    };
  }, [
    leadId,
    lead?.id,
    lead?._id,
    lead?.convertedQuoteId,
    lead?.tracking?.conversionData?.quoteId,
    lead?.automatedPricing?.quoteId,
    lead?.updatedAt,
  ]);

  const leadForOps = useMemo(() => {
    if (!lead) return null;
    if (!trustedLinkedQuote) return lead;
    return { ...lead, __linkedQuote: trustedLinkedQuote };
  }, [lead, trustedLinkedQuote]);

  const handleConvertToOpportunity = useCallback(async () => {
    setConvertingToOpportunity(true);
    try {
      const result = await convertLeadToOpportunity(leadId);
      if (result.success) {
        setNotification({
          open: true,
          message:
            result.message ||
            `Opportunity ${result.opportunityNumber || ""} ready`,
          severity: "success",
        });
        refetch();
        if (result.opportunityId) {
          navigate(`/opportunities/${result.opportunityId}`);
        }
      } else {
        setNotification({
          open: true,
          message: result.error || "Failed to convert to opportunity",
          severity: "error",
        });
      }
    } catch (err) {
      setNotification({
        open: true,
        message: err.message || "Failed to convert to opportunity",
        severity: "error",
      });
    } finally {
      setConvertingToOpportunity(false);
    }
  }, [leadId, navigate, refetch]);

  // Match QuoteDetailsView: keep shell mounted; only block when we have no lead yet
  if (loading && !lead) {
    return (
      <Container maxWidth="lg">
        <Box
          py={3}
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error && !lead) {
    return (
      <Container maxWidth="lg">
        <Box py={3}>
          <Alert severity="error">{error}</Alert>
          <Box mt={2}>
            <Button startIcon={<ArrowBack />} onClick={handleBack}>
              Back to Leads
            </Button>
          </Box>
        </Box>
      </Container>
    );
  }

  if (!lead) {
    return (
      <Container maxWidth="lg">
        <Box py={3}>
          <Alert severity="warning">Lead not found</Alert>
          <Box mt={2}>
            <Button startIcon={<ArrowBack />} onClick={handleBack}>
              Back to Leads
            </Button>
          </Box>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 2, md: 3 } }}>
      <Box py={3}>
        {error && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Could not refresh lead: {error}
          </Alert>
        )}
        {loading && (
          <Box display="flex" justifyContent="flex-end" mb={1}>
            <CircularProgress size={20} />
          </Box>
        )}
        {/* Header — Back + actions; route title lives in LeadJobSummaryCard */}
        <Box mb={2}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="flex-start"
            flexWrap="wrap"
            gap={2}
            mb={1.5}
          >
            <Box>
              <Button
                startIcon={<ArrowBack />}
                onClick={handleBack}
                sx={{ mb: 0, px: 0 }}
              >
                Back to Leads
              </Button>
            </Box>
            <LeadActionToolbar
              lead={lead}
              isEditing={isEditing}
              isLeadConverted={isLeadConverted}
              getConvertedQuoteId={getConvertedQuoteId}
              getOpportunityId={getOpportunityId}
              convertingToOpportunity={convertingToOpportunity}
              isDownloadingZip={isDownloadingZip}
              isDownloadingPdf={isDownloadingPdf}
              onAccept={() => handleAction("accept")}
              onReject={() => handleAction("reject")}
              onConvert={() => handleAction("convert")}
              onConvertOpportunity={handleConvertToOpportunity}
              onViewOpportunity={(id) => navigate(`/opportunities/${id}`)}
              onViewQuote={(quoteId) => {
                if (quoteId) navigate(`/quotes/${quoteId}`);
              }}
              onEdit={handleEdit}
              onSave={handleSave}
              onCancelEdit={handleCancel}
              onTemplate={() => setTemplateDialog(true)}
              onDownload={handleDownloadClick}
              onDownloadPdf={handleDownloadPdf}
              onWhatsApp={handleShareOnWhatsApp}
              onPublicCopy={() => setPublicCopyOpen(true)}
              onFollowUp={() =>
                navigate(`/followups/new?entityType=lead&entityId=${leadId}`)
              }
              onFollowUpNavigate={handleFollowUpNavigate}
              onAssignProvider={() => {
                setProvidersExpanded(true);
                navigate(`/leads/${leadId}?panel=bidding`);
                requestAnimationFrame(() => {
                  document
                    .getElementById("lead-bidding-anchor")
                    ?.scrollIntoView({ behavior: "smooth", block: "start" });
                });
              }}
              updateLoading={updateLoading}
            />
          </Box>

          <JobJourneyStrip
            current="lead"
            customerId={jobJourneyLinks.customerId}
            leadId={jobJourneyLinks.leadId}
            quoteId={
              jobJourneyLinks.leadId &&
              String(jobJourneyLinks.leadId) === String(leadId)
                ? jobJourneyLinks.quoteId
                : trustedQuoteId
            }
            invoiceId={jobJourneyLinks.invoiceId}
            orderId={jobJourneyLinks.orderId}
            customerDisplay={jobJourneyLinks.customerDisplay}
            leadDisplay={jobJourneyLinks.leadDisplay}
            quoteDisplay={jobJourneyLinks.quoteDisplay}
            invoiceDisplay={jobJourneyLinks.invoiceDisplay}
            orderDisplay={jobJourneyLinks.orderDisplay}
          />
          <Box mt={1.5} display="flex" justifyContent="flex-end">
            <EstimatePricingFab
              leadId={leadId}
              quoteId={trustedQuoteId}
              formSnapshot={{
                from: lead?.moving_from || lead?.from,
                to: lead?.moving_to || lead?.to,
                serviceType:
                  lead?.qualification?.serviceType || lead?.serviceType,
                items: lead?.items,
                itemsList: formatLeadItemsList(lead?.items),
              }}
            />
          </Box>
        </Box>

        <UnsafeAreaAlert matches={lead.unsafeAreaMatches} variant="mui" />


        {!isFulfilmentDiscovery && !isEditing && (
          <>
            <Box
              display="flex"
              alignItems="center"
              gap={1.5}
              flexWrap="wrap"
              mb={2}
            >
              <Typography variant="h5" component="h1" sx={{ fontWeight: 700, m: 0 }}>
                {lead.name || "Unnamed lead"}
              </Typography>
              <OperationalStatusBadge lead={leadForOps || lead} />
            </Box>

            <LeadValueProvidersStrip
              lead={leadForOps || lead}
              quote={trustedLinkedQuote}
              refreshKey={providersRefreshKey}
              onInviteToBid={() => {
                setProvidersExpanded(true);
                navigate(`/leads/${leadId}?panel=bidding`);
                requestAnimationFrame(() => {
                  document
                    .getElementById("lead-bidding-anchor")
                    ?.scrollIntoView({ behavior: "smooth", block: "start" });
                });
              }}
              onAssign={() => {
                setProvidersExpanded(true);
                navigate(`/leads/${leadId}?panel=bidding`);
                requestAnimationFrame(() => {
                  document
                    .getElementById("lead-bidding-anchor")
                    ?.scrollIntoView({ behavior: "smooth", block: "start" });
                });
              }}
              onViewAllProviders={() => {
                setProvidersExpanded(true);
                requestAnimationFrame(() => {
                  document
                    .getElementById("lead-bidding-anchor")
                    ?.scrollIntoView({ behavior: "smooth", block: "start" });
                });
              }}
            />

            <LeadFactsRow lead={leadForOps || lead} quote={trustedLinkedQuote} />
          </>
        )}

        {/* COCKPIT_END_MARKER */}

        {/* Lead identity / edit header (fulfilment / fallback) */}
        {(isFulfilmentDiscovery || isEditing) && (
        <Card elevation={1} sx={{ mb: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                mb: 2,
              }}
            >
              <Box>
                <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
                  {isEditing ? (
                    <TextField
                      fullWidth
                      label="Name"
                      value={editData.name || ""}
                      onChange={(e) =>
                        setEditData({ ...editData, name: e.target.value })
                      }
                      variant="standard"
                      sx={{ mt: 1 }}
                    />
                  ) : (
                    lead.name || "Unnamed Lead"
                  )}
                </Typography>
                {lead.referenceNumber && (
                  <Typography variant="body2" color="text.secondary">
                    Reference: {lead.referenceNumber}
                  </Typography>
                )}
                <Typography variant="subtitle2" color="text.secondary">
                  Customer & qualification
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  flexWrap: "wrap",
                  alignItems: "center",
                }}
              >
                {!isFulfilmentDiscovery && (
                  <ConfigProvider theme={bubakiiTheme}>
                    <LeadCompletenessIndicator
                      lead={lead}
                      onCheck={() => refetch()}
                    />
                  </ConfigProvider>
                )}
                <OperationalStatusBadge lead={leadForOps || lead} />
                <Chip
                  label={formatLeadCategory(leadCategory)}
                  size="small"
                  variant="outlined"
                />
                {lead.qualification?.category && (
                  <Chip
                    label={
                      lead.qualification.category === "local"
                        ? "Local"
                        : lead.qualification.category === "mid_distance"
                          ? "Mid-Distance"
                          : lead.qualification.category ===
                              "long_distance_share_load"
                            ? "Long-Distance"
                            : "Special Load"
                    }
                    size="small"
                    variant="outlined"
                    color="info"
                  />
                )}
                {lead.qualification?.serviceType && (
                  <Chip
                    label={formatServiceTypeLabel(
                      lead.qualification.serviceType
                    )}
                    size="small"
                    variant="outlined"
                    color="primary"
                  />
                )}
                {(lead.qualification?.pricingAdjusted ||
                  (Array.isArray(lead.qualification?.droppedExtras) &&
                    lead.qualification.droppedExtras.length > 0)) && (
                  <Chip
                    label="Pricing adjusted"
                    size="small"
                    color="warning"
                    variant="outlined"
                  />
                )}
                {lead.publishedTripId && (
                  <SharedLoadLeadBadge publishedTripId={lead.publishedTripId} />
                )}
              </Box>
            </Box>
          </CardContent>
        </Card>
        )}

        {/* Customer Information Section — skip when already in cockpit */}
        {lead.customerId && isFulfilmentDiscovery && (
          <Card elevation={1} sx={{ mb: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
              >
                <Typography variant="h6">Customer Information</Typography>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => navigate(`/customers/${lead.customerId}`)}
                >
                  View Customer Details
                </Button>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Name
                  </Typography>
                  <Typography variant="body1">{lead.name || "N/A"}</Typography>
                </Grid>
                {lead.phone && (
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Phone
                    </Typography>
                    <Typography variant="body1">
                      {formatPhone(lead.phone)}
                    </Typography>
                  </Grid>
                )}
                {lead.contactInfo?.email && (
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Email
                    </Typography>
                    <Typography variant="body1">
                      {lead.contactInfo.email}
                    </Typography>
                  </Grid>
                )}
                {lead.contactInfo?.address && (
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Address
                    </Typography>
                    <Typography variant="body1">
                      {lead.contactInfo.address}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Customer notes — restored for moving leads after cockpit removal */}
        {lead.customerId && (
          <Box mb={3}>
            <ConfigProvider theme={bubakiiTheme}>
              <CustomerNotesPanel
                customerId={lead.customerId}
                context="lead"
                relatedId={lead.id}
                entityType="lead"
              />
            </ConfigProvider>
          </Box>
        )}

        {showFulfilmentDiscovery && (
          <Card elevation={1} sx={{ mb: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                Fulfilment Discovery
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Company
                  </Typography>
                  <Typography variant="body1">
                    {fulfilmentDiscovery.companyName || "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Provinces Served
                  </Typography>
                  <Typography variant="body1">
                    {fulfilmentDiscovery.provincesServed?.join(", ") || "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Monthly Deliveries
                  </Typography>
                  <Typography variant="body1">
                    {fulfilmentDiscovery.estimatedMonthlyDeliveries || "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Current Delivery Method
                  </Typography>
                  <Typography variant="body1">
                    {fulfilmentDiscovery.currentDeliveryMethod || "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Preferred Contact
                  </Typography>
                  <Typography variant="body1">
                    {fulfilmentDiscovery.preferredContactMethod || "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Biggest Fulfilment Challenge
                  </Typography>
                  <Typography variant="body1">
                    {fulfilmentDiscovery.biggestChallenge || "N/A"}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Attachments from PWA / WhatsApp media */}
        {!isFulfilmentDiscovery && (
          <Card elevation={1} sx={{ mb: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Attachments ({mediaItems.length})
              </Typography>
              {mediaItems.length > 0 ? (
                <MediaItemsGallery
                  items={mediaItems}
                  entityId={leadId}
                  entityType="leads"
                  maxItems={50}
                />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No photos or documents attached yet. Customer uploads from the
                  quote form appear here after submission.
                </Typography>
              )}
            </CardContent>
          </Card>
        )}

        {/* Items - text only; editable when in edit mode */}
        <Card elevation={1} sx={{ mb: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 2 }}
            >
              <Typography variant="h6">
                {isFulfilmentDiscovery ? "Products Manufactured" : "Items"} (
                {isEditing ? editData.items?.length || 0 : tableItems.length})
              </Typography>
              <Box display="flex" gap={1} alignItems="center">
                {!isEditing &&
                  !isFulfilmentDiscovery &&
                  lead?.phone &&
                  tableItems.length > 0 && (
                  <Button
                    startIcon={<WhatsApp />}
                    onClick={handleConfirmItemsOnWhatsApp}
                    color="success"
                    variant="outlined"
                    size="small"
                  >
                    Request Confirmation
                  </Button>
                )}
                {isEditing && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      const next = [...(editData.items || []), ""];
                      setEditData({ ...editData, items: next });
                    }}
                  >
                    Add Item
                  </Button>
                )}
              </Box>
            </Box>

            {!isEditing ? (
              <>
                {tableItems.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No items provided
                  </Typography>
                ) : (
                  <TableContainer
                    component={Paper}
                    variant="outlined"
                    sx={{
                      "@media print": {
                        "& thead": { display: "table-header-group" },
                        "& tr": { breakInside: "avoid", pageBreakInside: "avoid" },
                      },
                    }}
                  >
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>
                            <Typography variant="subtitle2" fontWeight="bold">
                              Item
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="subtitle2" fontWeight="bold">
                              Description
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="subtitle2" fontWeight="bold">
                              Quantity
                            </Typography>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {tableItems.map((item, idx) => (
                          <TableRow key={`item-${idx}`} hover>
                            <TableCell
                              sx={{
                                wordBreak: "break-word",
                                overflowWrap: "anywhere",
                              }}
                            >
                              <Typography variant="body2">
                                {item.item}
                              </Typography>
                            </TableCell>
                            <TableCell
                              sx={{
                                wordBreak: "break-word",
                                overflowWrap: "anywhere",
                              }}
                            >
                              <Typography variant="body2">
                                {item.description}
                              </Typography>
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{ whiteSpace: "nowrap" }}
                            >
                              <Typography variant="body2">
                                {item.quantity}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
                {!isFulfilmentDiscovery &&
                  lead?.phone &&
                  tableItems.length > 0 && (
                  <Box
                    sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}
                  >
                    <Button
                      startIcon={<WhatsApp />}
                      onClick={handleConfirmItemsOnWhatsApp}
                      color="success"
                      variant="outlined"
                      size="small"
                    >
                      Request Items Confirmation via WhatsApp
                    </Button>
                  </Box>
                )}
              </>
            ) : (
              <Grid container spacing={1}>
                {(editData.items || []).map((val, idx) => (
                  <Grid item xs={12} key={idx}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <TextField
                        fullWidth
                        size="small"
                        label={`Item ${idx + 1}`}
                        value={val}
                        onChange={(e) => {
                          const next = [...editData.items];
                          next[idx] = e.target.value;
                          setEditData({ ...editData, items: next });
                        }}
                      />
                      <Button
                        size="small"
                        color="error"
                        variant="outlined"
                        onClick={() => {
                          const next = (editData.items || []).filter(
                            (_, i) => i !== idx
                          );
                          setEditData({ ...editData, items: next });
                        }}
                      >
                        Remove
                      </Button>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            )}
          </CardContent>
        </Card>

        {/* W15: Ops processing timeline (drawer on desktop/mobile) */}
        {leadId && !isFulfilmentDiscovery && (
          <LeadOpsActivitySection leadId={leadId} />
        )}

        {/* SOP #1: Qualification Information */}
        {(lead.qualification || isEditing) && (
          <Card elevation={1} sx={{ mb: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                Qualification Information
              </Typography>
              <Grid container spacing={2}>
                {lead.qualification?.distance?.km && (
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Distance
                    </Typography>
                    <Typography variant="body1">
                      {lead.qualification.distance.min !== undefined &&
                      lead.qualification.distance.max !== undefined &&
                      lead.qualification.distance.min !==
                        lead.qualification.distance.max
                        ? `${lead.qualification.distance.min}–${lead.qualification.distance.max} km`
                        : `${lead.qualification.distance.km} km`}
                    </Typography>
                  </Grid>
                )}
                {lead.qualification?.category && (
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Category
                    </Typography>
                    <Typography variant="body1">
                      {lead.qualification.category === "local"
                        ? "Local Move"
                        : lead.qualification.category === "mid_distance"
                          ? "Mid-Distance"
                          : lead.qualification.category ===
                              "long_distance_share_load"
                            ? "Long-distance / Shared load"
                            : "Special Load"}
                    </Typography>
                  </Grid>
                )}
                {lead.qualification?.serviceType && (
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Service type
                    </Typography>
                    <Typography variant="body1">
                      {formatServiceTypeLabel(lead.qualification.serviceType)}
                    </Typography>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <LeadElementChecklist
                    mode={isEditing ? "edit" : "readonly"}
                    elements={manifestElements}
                    confirmations={
                      isEditing
                        ? editData.elementConfirmations
                        : lead.qualification?.elementConfirmations
                    }
                    pricingSelections={lead.qualification?.pricingSelections}
                    card={
                      manifestCard ||
                      lead.qualification?.pricingSnapshot?.card ||
                      null
                    }
                    loading={manifestLoading}
                    error={manifestError}
                    onChange={(next) =>
                      setEditData((prev) => ({
                        ...prev,
                        elementConfirmations: next,
                      }))
                    }
                  />
                </Grid>

                {!(
                  Array.isArray(lead.qualification?.elementConfirmations) &&
                  lead.qualification.elementConfirmations.length > 0
                ) &&
                  !(
                    Array.isArray(lead.qualification?.pricingSelections) &&
                    lead.qualification.pricingSelections.length > 0
                  ) &&
                  lead.qualification?.accessibility && (
                  <>
                    {(lead.qualification.accessibility.pickupFloors ||
                      lead.qualification.accessibility.pickupStairs) && (
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Pickup
                        </Typography>
                        <Typography variant="body1">
                          {lead.qualification.accessibility.pickupFloors
                            ? `Floor ${lead.qualification.accessibility.pickupFloors}`
                            : ""}
                          {lead.qualification.accessibility.pickupStairs
                            ? " (Stairs)"
                            : ""}
                        </Typography>
                      </Grid>
                    )}
                    {(lead.qualification.accessibility.dropoffFloors ||
                      lead.qualification.accessibility.dropoffStairs) && (
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Drop-off
                        </Typography>
                        <Typography variant="body1">
                          {lead.qualification.accessibility.dropoffFloors
                            ? `Floor ${lead.qualification.accessibility.dropoffFloors}`
                            : ""}
                          {lead.qualification.accessibility.dropoffStairs
                            ? " (Stairs)"
                            : ""}
                        </Typography>
                      </Grid>
                    )}
                    {lead.qualification.accessibility.estateAccess && (
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Estate Access
                        </Typography>
                        <Typography variant="body1">
                          {lead.qualification.accessibility.estateName || "Yes"}
                        </Typography>
                      </Grid>
                    )}
                    {lead.qualification.accessibility.accessRestrictions && (
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Access Restrictions
                        </Typography>
                        <Typography variant="body1">
                          {lead.qualification.accessibility.accessRestrictions}
                        </Typography>
                      </Grid>
                    )}
                  </>
                )}
                {!(
                  Array.isArray(lead.qualification?.elementConfirmations) &&
                  lead.qualification.elementConfirmations.length > 0
                ) &&
                  !(
                    Array.isArray(lead.qualification?.pricingSelections) &&
                    lead.qualification.pricingSelections.length > 0
                  ) &&
                  lead.qualification?.items?.hasFragileItems && (
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Fragile Items
                    </Typography>
                    <Typography variant="body1">Yes</Typography>
                    {lead.qualification.items.fragileItemsList?.length > 0 && (
                      <Typography variant="body2" color="text.secondary">
                        {lead.qualification.items.fragileItemsList.join(", ")}
                      </Typography>
                    )}
                  </Grid>
                )}
                {!(
                  Array.isArray(lead.qualification?.elementConfirmations) &&
                  lead.qualification.elementConfirmations.length > 0
                ) &&
                  !(
                    Array.isArray(lead.qualification?.pricingSelections) &&
                    lead.qualification.pricingSelections.length > 0
                  ) &&
                  (lead.qualification?.items?.needsBoxes ||
                  lead.qualification?.items?.needsBubbleWrap ||
                  lead.qualification?.items?.needsProtectiveBlankets) && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Supplies Needed:
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                        mt: 1,
                      }}
                    >
                      {lead.qualification.items.needsBoxes && (
                        <Typography variant="body2">
                          Boxes: {lead.qualification.items.boxesQuantity || 1}{" "}
                          units
                        </Typography>
                      )}
                      {lead.qualification.items.needsBubbleWrap && (
                        <Typography variant="body2">
                          Bubble Wrap:{" "}
                          {lead.qualification.items.bubbleWrapQuantity || 1}{" "}
                          meters
                        </Typography>
                      )}
                      {lead.qualification.items.needsProtectiveBlankets && (
                        <Typography variant="body2">
                          Protective Blankets: Yes
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* SOP #1: Source Information */}
        {lead.source && typeof lead.source === "object" && (
          <Card elevation={1} sx={{ mb: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                Source Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Source Type
                  </Typography>
                  <Typography variant="body1">
                    {lead.source.type
                      ? lead.source.type
                          .split("_")
                          .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                          .join(" ")
                      : "N/A"}
                  </Typography>
                </Grid>
                {lead.source.adCampaign && (
                  <>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Platform
                      </Typography>
                      <Typography variant="body1">
                        {lead.source.adCampaign.platform || "N/A"}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Campaign
                      </Typography>
                      <Typography variant="body1">
                        {lead.source.adCampaign.campaignName || "N/A"}
                      </Typography>
                    </Grid>
                    {lead.source.adCampaign.adLink && (
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Ad Link
                        </Typography>
                        <Typography
                          variant="body2"
                          component="a"
                          href={lead.source.adCampaign.adLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ color: "primary.main", wordBreak: "break-all" }}
                        >
                          {lead.source.adCampaign.adLink}
                        </Typography>
                      </Grid>
                    )}
                  </>
                )}
                {lead.source.website && (
                  <>
                    {lead.source.website.landingPage && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Landing Page
                        </Typography>
                        <Typography
                          variant="body2"
                          component="a"
                          href={lead.source.website.landingPage}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ color: "primary.main", wordBreak: "break-all" }}
                        >
                          {lead.source.website.landingPage}
                        </Typography>
                      </Grid>
                    )}
                    {lead.source.website.utmParams && (
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" color="text.secondary">
                          UTM Parameters
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            gap: 1,
                            flexWrap: "wrap",
                            mt: 1,
                          }}
                        >
                          {lead.source.website.utmParams.utm_source && (
                            <Chip
                              label={`Source: ${lead.source.website.utmParams.utm_source}`}
                              size="small"
                            />
                          )}
                          {lead.source.website.utmParams.utm_medium && (
                            <Chip
                              label={`Medium: ${lead.source.website.utmParams.utm_medium}`}
                              size="small"
                            />
                          )}
                          {lead.source.website.utmParams.utm_campaign && (
                            <Chip
                              label={`Campaign: ${lead.source.website.utmParams.utm_campaign}`}
                              size="small"
                            />
                          )}
                        </Box>
                      </Grid>
                    )}
                  </>
                )}
                {lead.source.referral && (
                  <>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Referrer Name
                      </Typography>
                      <Typography variant="body1">
                        {lead.source.referral.referrerName || "N/A"}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Referrer Contact
                      </Typography>
                      <Typography variant="body1">
                        {lead.source.referral.referrerContact || "N/A"}
                      </Typography>
                    </Grid>
                    {lead.source.referral.referralCode && (
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Referral Code
                        </Typography>
                        <Typography variant="body1">
                          {lead.source.referral.referralCode}
                        </Typography>
                      </Grid>
                    )}
                  </>
                )}
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Lead Information */}
        <Card elevation={1} sx={{ mb: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
              Lead Information
            </Typography>
            <Grid container spacing={2}>
              {!isEditing ? (
                <>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Contact
                    </Typography>
                    <Typography variant="body1">
                      {formatPhone(lead.phone)}
                    </Typography>
                  </Grid>
                  {(lead.contactInfo?.email || lead.email) && (
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Email
                      </Typography>
                      <Typography variant="body1">
                        {lead.contactInfo?.email || lead.email}
                      </Typography>
                    </Grid>
                  )}
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="subtitle2" color="text.secondary">
                      From
                    </Typography>
                    <Typography variant="body1">
                      {lead.moving_from || "N/A"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="subtitle2" color="text.secondary">
                      To
                    </Typography>
                    <Typography variant="body1">
                      {lead.moving_to || "N/A"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Move Date
                    </Typography>
                    <Typography variant="body1">
                      {lead.move_date || "N/A"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Created
                    </Typography>
                    <Typography variant="body1">
                      {formatDate(lead.createdAt)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Status
                    </Typography>
                    <Chip
                      label={getStatusLabel(lead.status)}
                      size="small"
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Type
                    </Typography>
                    <Chip
                      label={formatLeadCategory(leadCategory)}
                      size="small"
                      variant="outlined"
                    />
                  </Grid>
                  {lead.qualification?.serviceType && (
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Service type
                      </Typography>
                      <Chip
                        label={formatServiceTypeLabel(
                          lead.qualification.serviceType
                        )}
                        size="small"
                        variant="outlined"
                        color="primary"
                      />
                    </Grid>
                  )}
                  {lead.publishedTripId && (
                    <Grid item xs={12}>
                      <SharedLoadLeadDetail
                        publishedTripId={lead.publishedTripId}
                      />
                    </Grid>
                  )}
                  {lead.notes && (
                    <Grid item xs={12}>
                      <LeadNotesPreview notes={lead.notes} />
                    </Grid>
                  )}
                </>
              ) : (
                <>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Name"
                      value={editData.name || ""}
                      onChange={(e) =>
                        setEditData({ ...editData, name: e.target.value })
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Phone Number"
                      value={editData.phone || ""}
                      onChange={(e) =>
                        setEditData({ ...editData, phone: e.target.value })
                      }
                      placeholder="+27821234567"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Email"
                      value={editData.contactInfo?.email || ""}
                      onChange={(e) =>
                        setEditData({
                          ...editData,
                          contactInfo: {
                            ...editData.contactInfo,
                            email: e.target.value,
                          },
                        })
                      }
                      type="email"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <GoogleAddressAutocomplete
                      label="Moving From"
                      value={editData.moving_from || ""}
                      onChange={(value) =>
                        setEditData((prev) => ({
                          ...prev,
                          moving_from: value,
                        }))
                      }
                      onPlaceSelect={(addressData) =>
                        setEditData((prev) => ({
                          ...prev,
                          moving_from: addressData.address,
                          pickupAddressData: addressData,
                        }))
                      }
                      placeholder="Start typing pickup address..."
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <GoogleAddressAutocomplete
                      label="Moving To"
                      value={editData.moving_to || ""}
                      onChange={(value) =>
                        setEditData((prev) => ({
                          ...prev,
                          moving_to: value,
                        }))
                      }
                      onPlaceSelect={(addressData) =>
                        setEditData((prev) => ({
                          ...prev,
                          moving_to: addressData.address,
                          deliveryAddressData: addressData,
                        }))
                      }
                      placeholder="Start typing delivery address..."
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Move Date"
                      type="date"
                      value={editData.move_date || ""}
                      onChange={(e) =>
                        setEditData({ ...editData, move_date: e.target.value })
                      }
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Category</InputLabel>
                      <Select
                        value={editData.category || ""}
                        onChange={(e) =>
                          setEditData({
                            ...editData,
                            category: e.target.value,
                          })
                        }
                        label="Category"
                      >
                        <MenuItem value="">Select Category</MenuItem>
                        <MenuItem value="moving">Moving</MenuItem>
                        <MenuItem value="delivery">Delivery</MenuItem>
                        <MenuItem value="storage">Storage</MenuItem>
                        <MenuItem value="residential">Residential</MenuItem>
                        <MenuItem value="commercial">Commercial</MenuItem>
                        <MenuItem value="industrial">Industrial</MenuItem>
                        <MenuItem value={FULFILMENT_DISCOVERY_CATEGORY}>
                          Fulfilment Discovery
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Status</InputLabel>
                      <Select
                        value={editData.status || lead.status}
                        onChange={(e) =>
                          setEditData({
                            ...editData,
                            status: e.target.value,
                          })
                        }
                        label="Status"
                      >
                        <MenuItem value="new">New</MenuItem>
                        <MenuItem value="accepted">Accepted</MenuItem>
                        <MenuItem value="rejected">Rejected</MenuItem>
                        <MenuItem value="converted">Converted</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Notes"
                      value={editData.notes || ""}
                      onChange={(e) =>
                        setEditData({ ...editData, notes: e.target.value })
                      }
                      multiline
                      rows={3}
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </CardContent>
        </Card>

        
        {!isEditing && !isFulfilmentDiscovery && (
          <Accordion
            id="lead-bidding-anchor"
            expanded={providersExpanded}
            onChange={(_, expanded) => setProvidersExpanded(expanded)}
            disableGutters
            elevation={1}
            sx={{ mb: 3, border: "1px solid", borderColor: "divider", "&:before": { display: "none" } }}
          >
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="subtitle1" fontWeight={700}>
                Providers & marketplace
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <BiddingPanel
                leadId={leadId}
                lead={lead}
                onLeadUpdated={() => {
                  refetch?.();
                  setProvidersRefreshKey((key) => key + 1);
                }}
                embedded
              />
            </AccordionDetails>
          </Accordion>
        )}


        {/* Submission Group Information */}
        {lead.submissionGroupId && (
          <Card elevation={1} sx={{ mb: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
              >
                <Typography variant="h6">Submission Group</Typography>
                <Button
                  startIcon={<Timeline />}
                  variant="outlined"
                  size="small"
                  onClick={() =>
                    navigate(`/leads/submission-groups/${lead.submissionGroupId}`)
                  }
                >
                  View Group Timeline
                </Button>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Group ID
                  </Typography>
                  <Typography variant="body1" sx={{ fontFamily: "monospace" }}>
                    {lead.submissionGroupId}
                  </Typography>
                </Grid>
                {lead.submissionSequence && (
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Submission Sequence
                    </Typography>
                    <Typography variant="body1">
                      #{lead.submissionSequence}
                      {lead.submissionSequence === 1 && (
                        <Chip
                          label="Original"
                          size="small"
                          color="primary"
                          sx={{ ml: 1 }}
                        />
                      )}
                      {lead.isResubmission && (
                        <Chip
                          label="Resubmission"
                          size="small"
                          color="warning"
                          sx={{ ml: 1 }}
                        />
                      )}
                    </Typography>
                  </Grid>
                )}
                {lead.adminReviewStatus && (
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Review Status
                    </Typography>
                    <Chip
                      label={
                        lead.adminReviewStatus === "pending"
                          ? "Pending Review"
                          : lead.adminReviewStatus === "accepted"
                            ? "Accepted"
                            : lead.adminReviewStatus === "rejected"
                              ? "Rejected"
                              : lead.adminReviewStatus
                      }
                      size="small"
                      color={
                        lead.adminReviewStatus === "pending"
                          ? "warning"
                          : lead.adminReviewStatus === "accepted"
                            ? "success"
                            : lead.adminReviewStatus === "rejected"
                              ? "error"
                              : "default"
                      }
                      variant="outlined"
                    />
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        )}
      </Box>

      {/* Action Dialog */}
      <Dialog
        open={actionDialog.open}
        onClose={handleActionCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {actionDialog.type === "accept" && "Accept Lead"}
          {actionDialog.type === "reject" && "Reject Lead"}
          {actionDialog.type === "convert" && "Convert to Quote"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {actionDialog.type === "reject" && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Reason for rejection"
                  value={actionDialog.data.reason || ""}
                  onChange={(e) =>
                    setActionDialog({
                      ...actionDialog,
                      data: { ...actionDialog.data, reason: e.target.value },
                    })
                  }
                  multiline
                  rows={2}
                  required
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Additional notes"
                value={actionDialog.data.notes || ""}
                onChange={(e) =>
                  setActionDialog({
                    ...actionDialog,
                    data: { ...actionDialog.data, notes: e.target.value },
                  })
                }
                multiline
                rows={3}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleActionCancel}>Cancel</Button>
          <Button
            onClick={handleActionConfirm}
            variant="contained"
            disabled={statusLoading}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Download Dialog */}
      <LeadDownloadDialog
        open={downloadDialog}
        onClose={() => setDownloadDialog(false)}
        lead={lead}
        onDownload={handleDownload}
      />

      <ShareLeadWhatsAppModal
        open={shareWhatsAppOpen}
        lead={leadForWhatsApp}
        onClose={() => setShareWhatsAppOpen(false)}
        onShared={() => {
          setNotification({
            open: true,
            message: "Opening WhatsApp to share lead summary",
            severity: "success",
          });
        }}
      />

      <CopyPublicLeadSummaryDialog
        open={publicCopyOpen}
        leadId={leadId}
        jobUrl={jobBoardUrl}
        onClose={() => setPublicCopyOpen(false)}
      />

      {/* SOP #1: Communication Template Generator Dialog */}
      <ConfigProvider theme={bubakiiTheme}>
        <CommunicationTemplateGenerator
          leadId={leadId}
          visible={templateDialog}
          onClose={() => setTemplateDialog(false)}
        />
      </ConfigProvider>

      {/* Notification */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleNotificationClose}
      >
        <Alert
          onClose={handleNotificationClose}
          severity={notification.severity}
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default LeadDetailsView;
